import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

const RosterManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    contractors: [],
    workOrders: [],
    designations: [],
    skills: [],
    locations: [],
  });

  // Create roster form data
  const [createFormData, setCreateFormData] = useState({
    rosterType: "weekly",
    name: "",
    startDate: dayjs(),
    month: dayjs().month() + 1,
    year: dayjs().year(),
    workOrderIds: [],
    contractorIds: [],
    filters: {
      designations: [],
      skills: [],
      locations: [],
      minExperience: "",
      maxExperience: "",
      availability: "available",
    },
  });

  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([]);

  const steps = [
    "Basic Info",
    "Select Work Orders",
    "Select Contractors",
    "Filter Workers",
    "Review & Create",
  ];

  const fetchRosters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/roster-management", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        },
      });
      setRosters(response.data.rosters || []);
    } catch (error) {
      enqueueSnackbar("Failed to load rosters", { variant: "error" });
      console.error("Error fetching rosters:", error);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get("/api/roster-management/meta/filters", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        },
      });
      setFilterOptions(response.data.filters || {});
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, []);

  useEffect(() => {
    fetchRosters();
    fetchFilterOptions();
  }, [fetchRosters, fetchFilterOptions]);

  const fetchFilteredWorkers = useCallback(async () => {
    try {
      const response = await axios.post(
        "/api/worker-details/filter",
        {
          contractorIds: createFormData.contractorIds,
          filters: createFormData.filters,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "x-tenant-id": localStorage.getItem("tenantId"),
          },
        }
      );
      setFilteredWorkers(response.data.workers || []);
    } catch (error) {
      console.error("Error fetching filtered workers:", error);
    }
  }, [createFormData.contractorIds, createFormData.filters]);

  const handleCreateRoster = async () => {
    try {
      setLoading(true);
      const endpoint =
        createFormData.rosterType === "weekly"
          ? "/api/roster-management/weekly"
          : "/api/roster-management/monthly";

      const payload = {
        ...createFormData,
        selectedWorkers: selectedWorkers.map((w) => w._id),
      };

      await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        },
      });

      setOpenCreateDialog(false);
      fetchRosters();
      resetCreateForm();
      enqueueSnackbar("Roster created successfully!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to create roster", { variant: "error" });
      console.error("Error creating roster:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetCreateForm = () => {
    setCreateFormData({
      rosterType: "weekly",
      name: "",
      startDate: dayjs(),
      month: dayjs().month() + 1,
      year: dayjs().year(),
      workOrderIds: [],
      contractorIds: [],
      filters: {
        designations: [],
        skills: [],
        locations: [],
        minExperience: "",
        maxExperience: "",
        availability: "available",
      },
    });
    setSelectedWorkers([]);
    setFilteredWorkers([]);
    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep === 3) {
      fetchFilteredWorkers();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleWorkerSelection = (worker, isSelected) => {
    if (isSelected) {
      setSelectedWorkers((prev) => [...prev, worker]);
    } else {
      setSelectedWorkers((prev) => prev.filter((w) => w._id !== worker._id));
    }
  };

  const downloadRoster = async (rosterId) => {
    try {
      const response = await axios.get(
        `/api/roster-management/${rosterId}/export/excel`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "x-tenant-id": localStorage.getItem("tenantId"),
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `roster_${rosterId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar("Failed to download roster", { variant: "error" });
      console.error("Error downloading roster:", error);
    }
  };

  const viewRosterDetails = async (rosterId) => {
    try {
      const response = await axios.get(`/api/roster-management/${rosterId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        },
      });
      setSelectedRoster(response.data.roster);
      setOpenViewDialog(true);
    } catch (error) {
      enqueueSnackbar("Failed to load roster details", { variant: "error" });
      console.error("Error fetching roster details:", error);
    }
  };

  // const markAttendance = async (rosterId, date, attendanceData) => {
  //   try {
  //     await axios.post(`/api/roster-management/${rosterId}/mark-attendance`, {
  //       date,
  //       attendanceData
  //     }, {
  //       headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  //     });

  //     // Refresh roster details
  //     viewRosterDetails(rosterId);
  //     enqueueSnackbar('Attendance marked successfully!', { variant: 'success' });
  //   } catch (error) {
  //     enqueueSnackbar('Failed to mark attendance', { variant: 'error' });
  //     console.error('Error marking attendance:', error);
  //   }
  // };

  const renderBasicInfoStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Roster Type</InputLabel>
          <Select
            value={createFormData.rosterType}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                rosterType: e.target.value,
              })
            }
            label="Roster Type"
          >
            <MenuItem value="weekly">Weekly Roster</MenuItem>
            <MenuItem value="monthly">Monthly Roster</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Roster Name"
          value={createFormData.name}
          onChange={(e) =>
            setCreateFormData({ ...createFormData, name: e.target.value })
          }
          placeholder="Leave empty for auto-generated name"
        />
      </Grid>

      {createFormData.rosterType === "weekly" ? (
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Week Start Date"
            value={createFormData.startDate}
            onChange={(date) =>
              setCreateFormData({ ...createFormData, startDate: date })
            }
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </Grid>
      ) : (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Month"
              type="number"
              value={createFormData.month}
              onChange={(e) =>
                setCreateFormData({
                  ...createFormData,
                  month: parseInt(e.target.value),
                })
              }
              inputProps={{ min: 1, max: 12 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Year"
              type="number"
              value={createFormData.year}
              onChange={(e) =>
                setCreateFormData({
                  ...createFormData,
                  year: parseInt(e.target.value),
                })
              }
              inputProps={{ min: 2020, max: 2030 }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );

  const renderWorkOrdersStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Select Work Orders for this Roster
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Work Orders</InputLabel>
          <Select
            multiple
            value={createFormData.workOrderIds}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                workOrderIds: e.target.value,
              })
            }
            label="Work Orders"
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const workOrder = filterOptions.workOrders.find(
                    (wo) => wo._id === value
                  );
                  return <Chip key={value} label={workOrder?.title || value} />;
                })}
              </Box>
            )}
          >
            {filterOptions.workOrders.map((workOrder) => (
              <MenuItem key={workOrder._id} value={workOrder._id}>
                <Checkbox
                  checked={
                    createFormData.workOrderIds.indexOf(workOrder._id) > -1
                  }
                />
                <Box>
                  <Typography variant="body1">{workOrder.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {workOrder.workLocation?.address}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Work Order Details */}
      {createFormData.workOrderIds.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Work Orders:
          </Typography>
          {createFormData.workOrderIds.map((woId) => {
            const workOrder = filterOptions.workOrders.find(
              (wo) => wo._id === woId
            );
            return workOrder ? (
              <Card key={woId} sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1 }}>
                  <Grid container alignItems="center">
                    <Grid item xs={6}>
                      <Typography variant="body2" fontWeight="medium">
                        {workOrder.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Location: {workOrder.workLocation?.address}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : null;
          })}
        </Grid>
      )}
    </Grid>
  );

  const renderContractorsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Select Contractors for this Roster
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Contractors</InputLabel>
          <Select
            multiple
            value={createFormData.contractorIds}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                contractorIds: e.target.value,
              })
            }
            label="Contractors"
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const contractor = filterOptions.contractors.find(
                    (c) => c._id === value
                  );
                  return (
                    <Chip
                      key={value}
                      label={contractor?.companyName || value}
                    />
                  );
                })}
              </Box>
            )}
          >
            {filterOptions.contractors.map((contractor) => (
              <MenuItem key={contractor._id} value={contractor._id}>
                <Checkbox
                  checked={
                    createFormData.contractorIds.indexOf(contractor._id) > -1
                  }
                />
                <Typography>{contractor.companyName}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderFiltersStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Filter Workers by Criteria
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Designations</InputLabel>
          <Select
            multiple
            value={createFormData.filters.designations}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                filters: {
                  ...createFormData.filters,
                  designations: e.target.value,
                },
              })
            }
            label="Designations"
          >
            {filterOptions.designations.map((designation) => (
              <MenuItem key={designation} value={designation}>
                {designation}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Skills</InputLabel>
          <Select
            multiple
            value={createFormData.filters.skills}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                filters: { ...createFormData.filters, skills: e.target.value },
              })
            }
            label="Skills"
          >
            {filterOptions.skills.map((skill) => (
              <MenuItem key={skill} value={skill}>
                {skill}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Locations</InputLabel>
          <Select
            multiple
            value={createFormData.filters.locations}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                filters: {
                  ...createFormData.filters,
                  locations: e.target.value,
                },
              })
            }
            label="Locations"
          >
            {filterOptions.locations.map((location) => (
              <MenuItem key={location} value={location}>
                {location}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Availability</InputLabel>
          <Select
            value={createFormData.filters.availability}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                filters: {
                  ...createFormData.filters,
                  availability: e.target.value,
                },
              })
            }
            label="Availability"
          >
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="partially_available">Partially Available</MenuItem>
            <MenuItem value="unavailable">Show All</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Minimum Experience (years)"
          type="number"
          value={createFormData.filters.minExperience}
          onChange={(e) =>
            setCreateFormData({
              ...createFormData,
              filters: {
                ...createFormData.filters,
                minExperience: e.target.value,
              },
            })
          }
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Maximum Experience (years)"
          type="number"
          value={createFormData.filters.maxExperience}
          onChange={(e) =>
            setCreateFormData({
              ...createFormData,
              filters: {
                ...createFormData.filters,
                maxExperience: e.target.value,
              },
            })
          }
        />
      </Grid>
    </Grid>
  );

  const renderReviewStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Review and Select Workers
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Found {filteredWorkers.length} workers matching your criteria. Select
          workers to include in the roster.
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedWorkers.length > 0 &&
                      selectedWorkers.length < filteredWorkers.length
                    }
                    checked={
                      filteredWorkers.length > 0 &&
                      selectedWorkers.length === filteredWorkers.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWorkers([...filteredWorkers]);
                      } else {
                        setSelectedWorkers([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell>Worker ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Contractor</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Skills</TableCell>
                <TableCell>Experience</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWorkers.map((worker) => {
                const isSelected = selectedWorkers.some(
                  (w) => w._id === worker._id
                );
                return (
                  <TableRow key={worker._id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) =>
                          handleWorkerSelection(worker, e.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell>{worker.workerId}</TableCell>
                    <TableCell>
                      {worker.personalDetails.firstName}{" "}
                      {worker.personalDetails.lastName}
                    </TableCell>
                    <TableCell>{worker.contractorId?.companyName}</TableCell>
                    <TableCell>
                      {worker.employmentDetails.designation}
                    </TableCell>
                    <TableCell>
                      {worker.employmentDetails.skills
                        ?.slice(0, 2)
                        .map((skill) => (
                          <Chip
                            key={skill}
                            label={skill}
                            size="small"
                            sx={{ mr: 0.5 }}
                          />
                        ))}
                    </TableCell>
                    <TableCell>
                      {worker.employmentDetails.experience || "N/A"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary">
          Selected {selectedWorkers.length} out of {filteredWorkers.length}{" "}
          workers
        </Typography>
      </Grid>
    </Grid>
  );

  const renderCreateDialog = () => (
    <Dialog
      open={openCreateDialog}
      onClose={() => setOpenCreateDialog(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Create New Roster</DialogTitle>
      <DialogContent>
        <Box sx={{ width: "100%", mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && renderBasicInfoStep()}
          {activeStep === 1 && renderWorkOrdersStep()}
          {activeStep === 2 && renderContractorsStep()}
          {activeStep === 3 && renderFiltersStep()}
          {activeStep === 4 && renderReviewStep()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button
            onClick={handleCreateRoster}
            variant="contained"
            disabled={loading || selectedWorkers.length === 0}
          >
            {loading ? "Creating..." : "Create Roster"}
          </Button>
        ) : (
          <Button onClick={handleNext} variant="contained">
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  const renderRosterDetailsDialog = () => {
    if (!selectedRoster) return null;

    return (
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          {selectedRoster.rosterName}
          <IconButton
            onClick={() => downloadRoster(selectedRoster._id)}
            sx={{ float: "right" }}
          >
            <DownloadIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
          >
            <Tab label="Overview" />
            <Tab label="Schedule" />
            <Tab label="Attendance" />
            <Tab label="Statistics" />
          </Tabs>

          {tabValue === 0 && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Basic Information
                    </Typography>
                    <Typography variant="body2">
                      Type: {selectedRoster.rosterType}
                    </Typography>
                    <Typography variant="body2">
                      Status: {selectedRoster.status}
                    </Typography>
                    <Typography variant="body2">
                      Period:{" "}
                      {dayjs(selectedRoster.period.startDate).format(
                        "DD/MM/YYYY"
                      )}{" "}
                      -{" "}
                      {dayjs(selectedRoster.period.endDate).format(
                        "DD/MM/YYYY"
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Statistics
                    </Typography>
                    <Typography variant="body2">
                      Total Workers:{" "}
                      {selectedRoster.statistics?.totalWorkers || 0}
                    </Typography>
                    <Typography variant="body2">
                      Working Days:{" "}
                      {selectedRoster.statistics?.totalWorkingDays || 0}
                    </Typography>
                    <Typography variant="body2">
                      Total Hours:{" "}
                      {selectedRoster.statistics?.totalWorkingHours || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box sx={{ mt: 2 }}>
              {selectedRoster.rosterSchedule?.map((day, dayIndex) => (
                <Accordion key={dayIndex}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      {dayjs(day.date).format("DD/MM/YYYY")} - {day.dayOfWeek}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {day.shifts?.map((shift, shiftIndex) => (
                      <Card key={shiftIndex} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {shift.shiftName} ({shift.startTime} -{" "}
                            {shift.endTime})
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            Workers: {shift.workers?.length || 0}
                          </Typography>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {shift.workers?.map((worker, workerIndex) => (
                              <Chip
                                key={workerIndex}
                                label={`${worker.workerName} (${worker.workerCode})`}
                                size="small"
                                color={
                                  worker.isPresent === true
                                    ? "success"
                                    : worker.isPresent === false
                                    ? "error"
                                    : "default"
                                }
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Roster Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
          >
            Create Roster
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Rosters List */}
        <Grid container spacing={3}>
          {rosters.map((roster) => (
            <Grid item xs={12} lg={6} key={roster._id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {roster.rosterName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {roster.rosterType.charAt(0).toUpperCase() +
                          roster.rosterType.slice(1)}{" "}
                        Roster
                      </Typography>
                    </Box>
                    <Chip
                      label={roster.status}
                      color={
                        roster.status === "active"
                          ? "success"
                          : roster.status === "completed"
                          ? "primary"
                          : roster.status === "cancelled"
                          ? "error"
                          : "default"
                      }
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" gutterBottom>
                    Period:{" "}
                    {dayjs(roster.period.startDate).format("DD/MM/YYYY")} -{" "}
                    {dayjs(roster.period.endDate).format("DD/MM/YYYY")}
                  </Typography>

                  <Typography variant="body2" gutterBottom>
                    Workers: {roster.statistics?.totalWorkers || 0} | Work
                    Orders: {roster.workOrders?.length || 0} | Contractors:{" "}
                    {roster.contractors?.length || 0}
                  </Typography>

                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                  >
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => viewRosterDetails(roster._id)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download Excel">
                      <IconButton
                        size="small"
                        onClick={() => downloadRoster(roster._id)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {renderCreateDialog()}
        {renderRosterDetailsDialog()}
      </Box>
    </LocalizationProvider>
  );
};

export default RosterManagement;
